import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Form } from '@amzn/awsui-components-react';

import {
    ActivityData,
    DeliveryInstructor,
    EditActivityData,
} from '../../../interfaces/activity';
import EditDeliveryDetailsFormSection from '../FormSections/EditDeliveryDetailsFormSection';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useNotifications } from '../../../../common/context/grimsbyNotifications';
import { useDispatch, useSelector } from 'react-redux';
import useFormValidation, {
    ValidationType,
} from '../../../../common/utils/formValidation';
import { OptionDefinition } from '@amzn/awsui-components-react-v3/polaris/internal/components/option/interfaces';
import {
    ActivityControlArrayValues,
    ActivityDeliveryDetailsFormSectionProps,
    ActivityInstructorControlArrayFormValues,
    ActivityModality,
    ActivitySessionControlArrayFormValues,
    getSessionAttributeEditorItem,
    InstructorAvailabilityData,
    SessionAttributeEditorItem,
} from '../Common/Common';
import { FormSectionMode } from '../../../../common/constants/forms';
import CancelModal, {
    CancelModalProps,
} from '../../../../common/components/CancelModal/CancelModal';
import { getDeliverySession } from '../Create/ActivityCreateForm';
import { updateSelectedActivity } from '../../../store/slices/selectedActivitySlice';
import { selectFeatures } from '../../../../common/store/slices/featureSlice';
import { checkFeature } from '../../../../common/utils/featureFlag';
import { handleScrollToError } from '../../../../common/utils/scrollToError';
import useFormDirtyState from '../../../../common/hooks/useFormDirtyState';
import {
    validateInstructorsForActivity,
    handleCheckInstructorActivityValidation,
    doesActivityHavePrimaryInstructor,
    doesActivityHaveOnlyOnePrimaryInstructor,
    doesActivityHaveSameDeliveryDetails,
    doesActivityHaveDifferentPrimaryInstructor,
    getActivityById,
    getSessionsWithDuplicatePrimary,
} from '../../../services/activity-service';

import EditDeliveryDetailsRescheduleModal from './EditDeliveryDetailsModals/EditDeliveryDetailsRescheduleModal';
import EditDeliveryDetailsAdjacentActivitiesModal from './EditDeliveryDetailsModals/EditDeliveryDetailsAdjacentActivitiesModal';
import EditDeliveryDetailsQualificationsModal from './EditDeliveryDetailsModals/EditDeliveryDetailsQualificationsModal';
import EditDeliveryDetailsConfirmNoPrimaryModal from './EditDeliveryDetailsModals/EditDeliveryDetailsConfirmNoPrimaryModal';
import EditDeliveryDetailsReassignModal from './EditDeliveryDetailsModals/EditDeliveryDetailsReassignModal';
import { UpdateActivityErrorMessage } from '../../Common/constants/flashContent';
import EditDeliveryDetailsOverrideAutoAssignmentModel from './EditDeliveryDetailsModals/EditDeliveryDetailsOverrideAutoAssignmentModel';
import EditDeliveryDetailsAutoAssignConflictModal from './EditDeliveryDetailsModals/EditDeliveryDetailsAutoAssignConflictModal';

const editvILTDeliveryDetailsValidationConfig: {
    [key in ValidationType]?: Array<
        keyof Omit<ActivityData, 'delivery_sessions'>
    >;
} = {
    required: [
        'course_name',
        'delivery_timezone',
        'delivery_city',
        'delivery_country',
        'delivery_region',
        'delivery_geo',
        'delivery_language',
    ],
};

const editILTDeliveryDetailsValidationConfig: {
    [key in ValidationType]?: Array<
        keyof Omit<ActivityData, 'delivery_sessions'>
    >;
} = {
    required: [
        'course_name',
        'delivery_timezone',
        'delivery_city',
        'delivery_country',
        'delivery_region',
        'delivery_geo',
        'delivery_language',
        'delivery_address_1',
        'delivery_postal_code',
    ],
};

export interface ActivityConfirmation {
    instructor?: any;
    type: string;
}

export const deliveryDetailsAttributeValidationConfig: {
    [activityControlArrayFormValuesKey in keyof ActivityControlArrayValues]: {
        [key in ValidationType]?: Array<
            keyof ActivityControlArrayValues[activityControlArrayFormValuesKey]
        >;
    };
} = {
    instructorAttributeEditorItems: {
        required: ['role', 'do_not_shuffle'],
    },
    sessionAttributeEditorItems: {
        required: [
            'dateString',
            'startTime',
            'endTime',
            'endTimeMeridiemFieldOption',
            'startTimeMeridiemFieldOption',
        ],
    },
};

const EditDeliveryDetailsForm = ({
    initialFormState,
}: {
    initialFormState: ActivityData;
}) => {
    const match = useRouteMatch<{ id: string }>();
    const history = useHistory();
    const { addNotification, removeAllNotifications } = useNotifications();
    const dispatch = useDispatch();
    const featureFlags = useSelector(selectFeatures);
    const [rescheduleReason, setRescheduleReason] =
        useState<OptionDefinition | null>(null);
    const [
        requiresInstructorActivityValidation,
        setRequiresInstructorActivityValidation,
    ] = useState(false);
    const [formValues, setFormValues] = useState(initialFormState);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isCancelModalVisible, setIsCancelModalVisible] = useState(false);
    const isDirty = useFormDirtyState(initialFormState, formValues);
    const {
        isInvalid,
        isControlArrayInvalid,
        errors,
        controlArrayErrors,
        validateForm,
        validateFormControlArray,
    } = useFormValidation<
        Omit<ActivityData, 'delivery_sessions'>,
        ActivitySessionControlArrayFormValues &
            ActivityInstructorControlArrayFormValues
    >();
    const [validatedInstructors, setValidatedInstructors] = useState<
        Array<any>
    >(initialFormState?.instructors?.map((instructor) => instructor.pk) ?? []);

    const [sessionAttributeEditorItems, setSessionAttributeEditorItems] =
        useState(
            formValues.delivery_sessions.map((item) =>
                getSessionAttributeEditorItem(
                    item,
                    formValues.delivery_timezone,
                ),
            ) || [],
        );

    const [instructorValidationResponse, setInstructorValidationResponse] =
        useState<Array<InstructorAvailabilityData>>([]);

    const [instructorAttributeEditorItems, setInstructorAttributeEditorItems] =
        useState<Array<DeliveryInstructor>>(
            formValues.instructors?.map((instructor) => ({ ...instructor })) ||
                [],
        );

    const [confirmationModals, setConfirmationModals] = useState<
        Array<ActivityConfirmation>
    >([]);

    const [confirmationModalIndex, setConfirmationModalIndex] = useState<
        number | null
    >(null);

    const [instructorItemErrors, setInstructorItemErrors] = useState<any>({});

    const [autoAssignInstructor, setAutoAssignInstructor] = useState<
        boolean | undefined
    >(initialFormState.auto_assign_instructor);

    const [overrideReason, setOverrideReason] =
        useState<OptionDefinition | null>(null);

    useEffect(() => {
        removeAllNotifications();
    }, [removeAllNotifications]);

    const handleFieldEvent = useCallback(
        (changes: Partial<Omit<EditActivityData, 'delivery_sessions'>>) => {
            setFormValues((values) => ({
                ...values,
                ...changes,
            }));
        },
        [],
    );

    const handleSessionItemEvent = (
        sessionItems: Array<SessionAttributeEditorItem>,
    ) => {
        setRequiresInstructorActivityValidation(true);
        setValidatedInstructors([]);
        setSessionAttributeEditorItems(sessionItems);
    };

    const handleSessionInstructorAssignmentEvent = (
        sessionItems: Array<SessionAttributeEditorItem>,
    ) => {
        setSessionAttributeEditorItems(sessionItems);
        setInstructorItemErrors({});
    };

    const handleInstructorItemEvent = (
        instructorItems: Array<DeliveryInstructor>,
    ) => {
        setInstructorAttributeEditorItems(instructorItems);
        setInstructorItemErrors({});
    };

    const handleAddInstructor = (instructorPk: string) => {
        setValidatedInstructors([...validatedInstructors, instructorPk]);
    };

    const handleAutoAssignInstructorChange = (
        autoAssignInstructor: boolean | undefined,
    ) => {
        setAutoAssignInstructor(autoAssignInstructor);
    };

    const validateInstructorAvailability = async ({
        delivery_country,
        delivery_timezone,
        delivery_sessions,
        delivery_language,
        course_name,
        auto_assign_instructor,
    }: ActivityData) => {
        // Determine if we should validate per-instructor based on session assignments
        const hasAnySessionWithInstructors = sessionAttributeEditorItems.some(
            (session) => session.instructors && session.instructors.length > 0,
        );

        if (hasAnySessionWithInstructors) {
            // Validate each instructor against only the sessions they are assigned to
            const validationPromises = instructorAttributeEditorItems
                .filter((instructor) => instructor.pk)
                .map((instructor) => {
                    // Determine which sessions this instructor is assigned to
                    const isAssignedToAnySession =
                        sessionAttributeEditorItems.some(
                            (s) =>
                                s.instructors &&
                                s.instructors.some(
                                    (inst) => inst.pk === instructor.pk,
                                ),
                        );

                    // If not explicitly assigned to any session, they're on all sessions
                    const instructorSessions = isAssignedToAnySession
                        ? sessionAttributeEditorItems.filter(
                              (s) =>
                                  s.instructors &&
                                  s.instructors.some(
                                      (inst) => inst.pk === instructor.pk,
                                  ),
                          )
                        : sessionAttributeEditorItems;

                    const instructorDeliverySessions = instructorSessions
                        .filter(
                            (item) =>
                                item.startTime &&
                                item.endTime &&
                                item.dateString,
                        )
                        .map((item) =>
                            getDeliverySession(item, delivery_timezone),
                        );

                    if (instructorDeliverySessions.length === 0)
                        return Promise.resolve([]);

                    return validateInstructorsForActivity({
                        id: match.params.id,
                        instructors: [instructor],
                        delivery_language,
                        delivery_country,
                        delivery_sessions: instructorDeliverySessions,
                        delivery_timezone,
                        course_name,
                        auto_assign_instructor,
                    });
                });

            const results = await Promise.all(validationPromises);
            const allResponses = results.flat();

            setInstructorValidationResponse(allResponses);
            return allResponses;
        }

        // Default: validate all instructors against all sessions
        const instructorValidationResponse =
            await validateInstructorsForActivity({
                id: match.params.id,
                instructors: instructorAttributeEditorItems,
                delivery_language: delivery_language,
                delivery_country: delivery_country,
                delivery_sessions,
                delivery_timezone: delivery_timezone,
                course_name: course_name,
                auto_assign_instructor: auto_assign_instructor,
            });
        setInstructorValidationResponse(instructorValidationResponse);
        return instructorValidationResponse;
    };

    const areSessionsInvalid = () => {
        const sessionsInvalid = validateFormControlArray(
            { sessionAttributeEditorItems, instructorAttributeEditorItems },
            deliveryDetailsAttributeValidationConfig,
        );

        return sessionsInvalid;
    };

    const handleSubmitEvent = async () => {
        setInstructorValidationResponse([]);
        setIsSubmitting(true);
        setInstructorItemErrors(null);
        const modality = formValues.activity_modality;
        let invalid = false;

        if (
            modality === ActivityModality.ILT ||
            modality === ActivityModality.Hybrid
        ) {
            invalid = validateForm(
                formValues,
                editILTDeliveryDetailsValidationConfig,
            );
        } else {
            invalid = validateForm(
                formValues,
                editvILTDeliveryDetailsValidationConfig,
            );
        }
        //for ILT, we need to validate address line 1 and postalcode
        const sessionArrayInvalid = areSessionsInvalid();
        // Validates that the instructor has no booked activities and available for dates.
        const validationResponse = await validateInstructorAvailability({
            ...formValues,
            delivery_sessions: sessionAttributeEditorItems
                .filter(
                    (item) => item.startTime && item.endTime && item.dateString,
                )
                .map((item) =>
                    getDeliverySession(item, formValues.delivery_timezone),
                ),
        });

        const isInstructorAvailable =
            validationResponse.filter((instructor) => {
                let availability = ['do_not_shuffle', 'reserved'];

                if (availability.includes(instructor.availability)) {
                    return true;
                }

                return false;
            }).length === 0;
        const isPrimaryInstructorReservedAndOthersAvailable =
            validationResponse.filter((instructor) => {
                if (instructor.availability === 'do_not_shuffle') {
                    return true;
                }

                if (instructor.availability === 'reserved') {
                    let deliveryInstructor =
                        instructorAttributeEditorItems.find(
                            (instructorAttributeEditorItem) =>
                                instructorAttributeEditorItem.pk ===
                                instructor.pk,
                        );
                    return !(
                        deliveryInstructor &&
                        deliveryInstructor.role === 'Primary'
                    );
                }

                return false;
            }).length === 0;

        // When we have the auto assign instructor option turned on, we don't want to stop editing just because the primary instructor is reserved.
        // Our auto assignment can trigger and find a new instructor.
        const isInstructorAvailabilityValid = autoAssignInstructor
            ? isInstructorAvailable ||
              isPrimaryInstructorReservedAndOthersAvailable
            : isInstructorAvailable;

        const isPerSessionAssignmentEnabled = checkFeature(
            '',
            { featureName: 'per-session-instructor-assignment' },
            featureFlags?.features,
        );

        const hasAnySessionWithInstructors = sessionAttributeEditorItems.some(
            (session) => session.instructors && session.instructors.length > 0,
        );

        const usePerSessionValidation =
            isPerSessionAssignmentEnabled && hasAnySessionWithInstructors;

        let hasDuplicatePrimary = false;

        if (usePerSessionValidation) {
            const sessionsWithDuplicatePrimary =
                getSessionsWithDuplicatePrimary(
                    sessionAttributeEditorItems,
                    instructorAttributeEditorItems,
                );

            if (sessionsWithDuplicatePrimary) {
                const sessionErrors: Record<string, { role: string }> = {};
                for (const [sessionIndex, instructorPk] of Object.entries(
                    sessionsWithDuplicatePrimary,
                )) {
                    sessionErrors[instructorPk] = {
                        role: `Limit of one primary instructor per session.`,
                    };
                }
                setInstructorItemErrors({
                    ...instructorItemErrors,
                    ...sessionErrors,
                });
                hasDuplicatePrimary = true;
            }
        } else {
            const duplicatePrimaryKey =
                doesActivityHaveOnlyOnePrimaryInstructor(
                    instructorAttributeEditorItems,
                );

            if (duplicatePrimaryKey) {
                setInstructorItemErrors({
                    ...instructorItemErrors,
                    [duplicatePrimaryKey as string]: {
                        role: 'Limit of one primary instructor.',
                    },
                });
                hasDuplicatePrimary = true;
            }
        }

        const isActivityFormInvalid =
            invalid ||
            sessionArrayInvalid ||
            !isInstructorAvailabilityValid ||
            hasDuplicatePrimary;

        if (isActivityFormInvalid) {
            handleScrollToError();
            setIsSubmitting(false);
        } else {
            // make sure last check validations are good
            let pendingActivityConfirmations =
                handleCheckInstructorActivityValidation(
                    validationResponse,
                    requiresInstructorActivityValidation,
                    formValues,
                    validatedInstructors,
                );

            // Remove reassign confirmation if primary instructor has a schedule conflict.
            // In this case, AIA will take care of assigning a new instructor.
            if (autoAssignInstructor) {
                let primary_instructor_pk = null;
                for (let instructor of instructorAttributeEditorItems) {
                    const { role: instructorRole, pk: instructorPk } =
                        instructor;
                    if (instructorRole === 'Primary') {
                        primary_instructor_pk = instructorPk;
                    }
                }
                if (primary_instructor_pk != null) {
                    const indexToRemove =
                        pendingActivityConfirmations.findIndex(
                            (confirmation) =>
                                confirmation.type === 'reassign' &&
                                confirmation.instructor.pk ===
                                    primary_instructor_pk,
                        );
                    if (indexToRemove !== -1) {
                        pendingActivityConfirmations.splice(indexToRemove, 1);
                    }
                }
            }

            if (
                !doesActivityHavePrimaryInstructor(
                    instructorAttributeEditorItems,
                )
            ) {
                pendingActivityConfirmations.push({
                    type: 'no_primary',
                });
            }

            if (requiresInstructorActivityValidation && !rescheduleReason) {
                pendingActivityConfirmations.push({
                    type: 'reschedule',
                    instructor: null,
                });
            }

            // Validate whether previously auto-assigned instructor has a manual override
            const isInstructorPreviouslyAutoAssigned =
                initialFormState.auto_assign_instructor;
            const isAutoAssignInstructorToggleDisabled =
                autoAssignInstructor === false;

            const isAutoAssignedInstructorOverridden =
                doesActivityHaveSameDeliveryDetails(
                    initialFormState,
                    formValues,
                ) &&
                doesActivityHaveDifferentPrimaryInstructor(
                    initialFormState,
                    instructorAttributeEditorItems,
                ) &&
                isInstructorPreviouslyAutoAssigned &&
                isAutoAssignInstructorToggleDisabled;

            if (isAutoAssignedInstructorOverridden) {
                pendingActivityConfirmations.push({
                    type: 'auto_assigned_instructor_overridden',
                    instructor: null,
                });
            }

            // Warn if auto-assign is enabled and the user has explicitly changed
            // instructor assignments (added/removed instructors or changed session assignments)
            const validInstructors = instructorAttributeEditorItems.filter(
                (i) => i.pk,
            );

            if (autoAssignInstructor && validInstructors.length > 0) {
                const initialPks = new Set(
                    initialFormState.instructors?.map((i) => i.pk) ?? [],
                );
                const currentPks = new Set(validInstructors.map((i) => i.pk));

                const instructorListChanged =
                    initialPks.size !== currentPks.size ||
                    Array.from(currentPks).some((pk) => !initialPks.has(pk));

                const sessionAssignmentsChanged =
                    sessionAttributeEditorItems.some((session) => {
                        const initialSession =
                            initialFormState.delivery_sessions?.find(
                                (s) => s.id === session.deliverySessionId,
                            );
                        const currentSessionPks = new Set(
                            session.instructors
                                ?.filter((i) => i.pk)
                                .map((i) => i.pk) ?? [],
                        );
                        const initialSessionPks = new Set(
                            initialSession?.instructors
                                ?.filter((i) => i.pk)
                                .map((i) => i.pk) ?? [],
                        );
                        return (
                            currentSessionPks.size !== initialSessionPks.size ||
                            Array.from(currentSessionPks).some(
                                (pk) => !initialSessionPks.has(pk),
                            )
                        );
                    });

                if (instructorListChanged || sessionAssignmentsChanged) {
                    pendingActivityConfirmations.push({
                        type: 'auto_assign_with_manual_instructors',
                    });
                }
            }

            if (pendingActivityConfirmations.length === 0) {
                handleConfirmSave();
            } else {
                // we have confirmations
                setConfirmationModals(pendingActivityConfirmations);
                setConfirmationModalIndex(0);
            }
        }
    };

    const handleConfirmationNext = () => {
        if (confirmationModalIndex === null) {
            handleConfirmSave();
            return;
        }

        if (confirmationModalIndex === confirmationModals.length - 1) {
            // we are good, confirm
            handleConfirmSave();
        } else {
            const nextIndex = confirmationModalIndex + 1;
            setConfirmationModalIndex(nextIndex);
        }
    };

    const cancelConfirmationModal = () => {
        handleScrollToError();
        setIsSubmitting(false);
        setConfirmationModals([]);
        setConfirmationModalIndex(null);
    };

    const handleConfirmSave = async () => {
        const shouldSyncPerSessionInstructors = checkFeature(
            '',
            { featureName: 'per-session-instructor-assignment' },
            featureFlags?.features,
        );

        // Populate each delivery session with instructor assignments
        const sessionsWithInstructors = shouldSyncPerSessionInstructors
            ? sessionAttributeEditorItems.map((session) => {
                  const sessionInstructors = session.instructors || [];

                  const instructorsForSession = instructorAttributeEditorItems
                      .map((instructor) => ({
                          pk: instructor.pk,
                          name: instructor.name,
                          email: instructor.email,
                          role: instructor.role,
                          type: instructor.type,
                          location: instructor.location,
                          do_not_shuffle: instructor.do_not_shuffle,
                      }))
                      .filter((instructor) => {
                          const assignedToAnySessions =
                              sessionAttributeEditorItems.some(
                                  (s) =>
                                      s.instructors &&
                                      s.instructors.some(
                                          (inst) => inst.pk === instructor.pk,
                                      ),
                              );
                          if (!assignedToAnySessions) {
                              return true;
                          }
                          return sessionInstructors.some(
                              (inst) => inst.pk === instructor.pk,
                          );
                      });

                  return {
                      ...session,
                      instructors: instructorsForSession,
                  };
              })
            : sessionAttributeEditorItems;

        const activityData: Partial<ActivityData> = {
            ...formValues,
            delivery_sessions: sessionsWithInstructors
                .filter(
                    (item) => item.startTime && item.endTime && item.dateString,
                )
                .map((item) =>
                    getDeliverySession(item, formValues.delivery_timezone),
                ),
            instructors: instructorAttributeEditorItems,
            auto_assign_instructor: autoAssignInstructor,
        };

        // Clear WebEx meeting fields - managed by VCMS Lambda
        // Only set to null if empty string (fixes validation bug)
        // Otherwise omit from payload to avoid triggering unnecessary VCMS processing
        if (activityData.v_ilt_id === '') {
            activityData.v_ilt_id = null;
        } else if (
            activityData.v_ilt_id === undefined ||
            activityData.v_ilt_id === null
        ) {
            delete activityData.v_ilt_id;
        }

        if (activityData.v_ilt_meeting_id === '') {
            activityData.v_ilt_meeting_id = null;
        } else if (
            activityData.v_ilt_meeting_id === undefined ||
            activityData.v_ilt_meeting_id === null
        ) {
            delete activityData.v_ilt_meeting_id;
        }

        // v_ilt_type: Keep in payload if explicitly set (needed for WebEx→Other transitions)
        // Only delete if undefined
        if (activityData.v_ilt_type === undefined) {
            delete activityData.v_ilt_type;
        }

        const result = await dispatch<any>(
            updateSelectedActivity(match.params.id, activityData),
        );

        removeAllNotifications();

        if (autoAssignInstructor) {
            const activityData = await getActivityById(match.params.id);
            const instructors =
                activityData?.activity && activityData.activity?.instructors;
            const hasPrimaryInstructor =
                doesActivityHavePrimaryInstructor(instructors);
            addNotification({
                id: `update-activity`,
                ...(result.isSuccessful
                    ? hasPrimaryInstructor
                        ? {
                              type: 'success',
                              content: 'The activity has been updated.',
                          }
                        : {
                              type: 'warning',
                              content:
                                  'The activity has been updated. No instructors available for auto assignment. Please manually assign an instructor.',
                          }
                    : {
                          type: 'error',
                          content: <UpdateActivityErrorMessage />,
                      }),
            });
        } else {
            addNotification({
                id: `update-activity`,
                ...(result.isSuccessful
                    ? {
                          type: 'success',
                          content: 'The activity has been updated.',
                      }
                    : {
                          type: 'error',
                          content: <UpdateActivityErrorMessage />,
                      }),
            });
        }

        if (result.isSuccessful) {
            navigateToDetailPage();
        } else {
            setIsSubmitting(false);
        }
    };

    const editDeliveryDetailsFormProps: ActivityDeliveryDetailsFormSectionProps & {
        handleAutoAssignInstructorChange: (
            autoAssignInstructor: boolean,
        ) => void;
    } = {
        formValues,
        errors,
        controlArrayErrors,
        handleFieldEvent,
        mode: FormSectionMode.Edit,
        handleSessionItemEvent,
        handleSessionInstructorAssignmentEvent,
        sessionAttributeEditorItems,
        handleInstructorItemEvent,
        instructorAttributeEditorItems,
        instructorValidationResponse,
        handleAddInstructor,
        instructorItemErrors,
        areSessionsInvalid,
        handleAutoAssignInstructorChange,
    };

    const navigateToDetailPage = () => {
        history.push({
            pathname: `/activities/${match.params.id}`,
        });
    };

    const cancelModalProps: CancelModalProps = {
        isCancelModalVisible,
        setIsCancelModalVisible,
        isSubmitting,
        onCancelConfirm: navigateToDetailPage,
        testPrefix: 'EditDeliveryDetails',
        isDirty,
    };

    const handleChangeRescheduleReason = (e: any) => {
        handleFieldEvent({
            reschedule_reason: e.label,
        });
        setRescheduleReason(e);
    };

    const handleOverrideReason = (e: any) => {
        handleFieldEvent({
            instructor_assignment_override_reason: e.label,
        });
        setOverrideReason(e);
    };

    useEffect(() => {
        if (isInvalid) {
            const modality = formValues.activity_modality;
            if (
                modality === ActivityModality.ILT ||
                modality === ActivityModality.Hybrid
            ) {
                validateForm(
                    formValues,
                    editILTDeliveryDetailsValidationConfig,
                );
            } else {
                validateForm(
                    formValues,
                    editvILTDeliveryDetailsValidationConfig,
                );
            }
        }

        if (isControlArrayInvalid) {
            validateFormControlArray(
                { sessionAttributeEditorItems, instructorAttributeEditorItems },
                deliveryDetailsAttributeValidationConfig,
            );
        }
    }, [
        isInvalid,
        formValues,
        validateForm,
        validateFormControlArray,
        sessionAttributeEditorItems,
        instructorAttributeEditorItems,
        isControlArrayInvalid,
    ]);

    return (
        <>
            <Form
                header={initialFormState.activity_name}
                data-testid="ActivityEditDeliveryDetailsForm"
                actions={
                    <div className="awsui-util-pt-xl">
                        <Button
                            variant="link"
                            data-testid="EditDeliveryDetailsFormCancel"
                            disabled={isSubmitting}
                            onClick={() => {
                                setIsCancelModalVisible(true);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            data-testid="EditDeliveryDetailsFormSave"
                            formAction="submit"
                            disabled={isSubmitting}
                            onClick={handleSubmitEvent}
                        >
                            {isSubmitting ? 'Saving' : 'Save'}
                        </Button>
                    </div>
                }
            >
                <EditDeliveryDetailsFormSection
                    {...editDeliveryDetailsFormProps}
                />
            </Form>
            <CancelModal {...cancelModalProps} />
            {confirmationModalIndex !== null &&
                confirmationModals.length > 0 &&
                confirmationModals.map((confirmation: any, index: number) => {
                    const { instructor } = confirmation;

                    switch (confirmation.type) {
                        case 'reschedule':
                            return (
                                <EditDeliveryDetailsRescheduleModal
                                    instructorConflict={{
                                        instructor: null,
                                    }}
                                    visible={index === confirmationModalIndex}
                                    onCancel={() => {
                                        cancelConfirmationModal();
                                    }}
                                    onConfirm={handleConfirmationNext}
                                    rescheduleReason={rescheduleReason}
                                    setRescheduleReason={
                                        handleChangeRescheduleReason
                                    }
                                    key={`reschedule-reasons-modal`}
                                />
                            );
                        case 'adjacent':
                            return (
                                <EditDeliveryDetailsAdjacentActivitiesModal
                                    instructorConflict={{
                                        instructor,
                                    }}
                                    visible={index === confirmationModalIndex}
                                    onCancel={() => {
                                        cancelConfirmationModal();
                                    }}
                                    onConfirm={() => {
                                        handleConfirmationNext();
                                    }}
                                    key={`delivery-details-modal`}
                                />
                            );
                        case 'reassign':
                            return (
                                <EditDeliveryDetailsReassignModal
                                    instructorConflict={{
                                        instructor,
                                    }}
                                    visible={index === confirmationModalIndex}
                                    onCancel={() => {
                                        cancelConfirmationModal();
                                    }}
                                    onConfirm={() => {
                                        handleConfirmationNext();
                                    }}
                                    handleFieldEvent={handleFieldEvent}
                                    key={`delivery-details-modal-${instructor.pk}-${index}`}
                                />
                            );
                        case 'qualification':
                            return (
                                <EditDeliveryDetailsQualificationsModal
                                    instructorConflict={{
                                        instructor,
                                    }}
                                    visible={index === confirmationModalIndex}
                                    onCancel={() => {
                                        cancelConfirmationModal();
                                    }}
                                    onConfirm={() => {
                                        handleConfirmationNext();
                                    }}
                                    formValues={formValues}
                                    missingQualifications={
                                        confirmation.missingQualifications
                                    }
                                    key={`qualifications-modal-${index}`}
                                />
                            );
                        case 'no_primary':
                            return (
                                <EditDeliveryDetailsConfirmNoPrimaryModal
                                    instructorConflict={{
                                        instructor,
                                    }}
                                    visible={index === confirmationModalIndex}
                                    onCancel={() => {
                                        cancelConfirmationModal();
                                    }}
                                    onConfirm={() => {
                                        handleConfirmationNext();
                                    }}
                                    isInstructorAutoAssigned={
                                        autoAssignInstructor
                                    }
                                    key={`no-primary-modal-${index}`}
                                />
                            );
                        case 'auto_assigned_instructor_overridden':
                            return (
                                <EditDeliveryDetailsOverrideAutoAssignmentModel
                                    visible={index === confirmationModalIndex}
                                    onCancel={() => {
                                        cancelConfirmationModal();
                                    }}
                                    onConfirm={() => {
                                        handleConfirmationNext();
                                    }}
                                    overrideReason={overrideReason}
                                    setOverrideReason={handleOverrideReason}
                                    key={`auto-assignment-override-modal-${index}`}
                                />
                            );
                        case 'auto_assign_with_manual_instructors':
                            return (
                                <EditDeliveryDetailsAutoAssignConflictModal
                                    visible={index === confirmationModalIndex}
                                    onCancel={() => {
                                        cancelConfirmationModal();
                                    }}
                                    onConfirm={() => {
                                        handleConfirmationNext();
                                    }}
                                    key={`auto-assign-conflict-modal-${index}`}
                                />
                            );
                        default:
                            return <></>;
                    }
                })}
        </>
    );
};

export default EditDeliveryDetailsForm;
